import React from 'react';
import { Plus, Trash2, X, Settings, Lock, Dice5, CreditCard, Package, Box, Puzzle, Grid3X3, Gamepad2, BookOpen, Gift, Layers, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker, getLocalizedField } from '../../utils/localizedContent';


// Tipos que no se pueden eliminar
const IMMUTABLE_TYPES = new Set(['board_game', 'tcg']);

const ICON_OPTIONS = [
  { id: 'dice',    Icon: Dice5,      label: 'Dado' },
  { id: 'cards',   Icon: CreditCard, label: 'Cartas' },
  { id: 'package', Icon: Package,    label: 'Caja' },
  { id: 'cube',    Icon: Box,        label: 'Cubo' },
  { id: 'puzzle',  Icon: Puzzle,     label: 'Puzzle' },
  { id: 'layers',  Icon: Layers,     label: 'Capas' },
  { id: 'gamepad', Icon: Gamepad2,   label: 'Mando' },
  { id: 'book',    Icon: BookOpen,   label: 'Libro' },
  { id: 'gift',    Icon: Gift,       label: 'Regalo' },
  { id: 'shop',    Icon: ShoppingBag,label: 'Tienda' },
  { id: 'grid',    Icon: Grid3X3,    label: 'Grid' },
];

function getIconComponent(id, size = 18) {
  const found = ICON_OPTIONS.find(o => o.id === id);
  if (!found) return <Package size={size} />;
  const { Icon } = found;
  return <Icon size={size} />;
}

export default function ProductTypeManager({ productTypes, onRefresh, lang, setMessage, onClose }) {
  const [newType, setNewType] = React.useState({ name: '', name_es: '', icon: 'package' });
  const l = createTextPicker(lang);

  const handleAdd = async () => {
    if (!newType.name.trim()) {
      setMessage(l('Escribe un nombre', 'Enter a name'));
      return;
    }
    try {
      await axios.post(`${API}/product-types`, newType);
      setNewType({ name: '', name_es: '', icon: 'package' });
      onRefresh();
      setMessage(l('Tipo creado', 'Type created'));
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  const handleDelete = async (typeId) => {
    if (!window.confirm(l('¿Eliminar este tipo de producto?', 'Delete this product type?'))) return;
    try {
      await axios.delete(`${API}/product-types/${typeId}`);
      onRefresh();
      setMessage(l('Tipo eliminado', 'Type deleted'));
    } catch (err) {
      setMessage(formatApiError(err));
    }
  };

  return (
    <div className="bg-[#12121A] border border-[#C9A962]/30 rounded-2xl p-6 mb-6" data-testid="type-manager">
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-[#C9A962] font-bold flex items-center gap-2">
          <Settings size={16} />
          {l('Gestionar Tipos de Producto', 'Manage Product Types')}
        </h3>
        <button onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X size={18} /></button>
      </div>

      {/* Existing types */}
      <div className="space-y-2 mb-6">
        {productTypes.map(type => {
          const isImmutable = IMMUTABLE_TYPES.has(type.type_id);
          return (
            <div key={type.type_id} className={`flex items-center justify-between border rounded-xl px-4 py-3 ${
              isImmutable ? 'bg-[#C9A962]/5 border-[#C9A962]/20' : 'bg-[#0A0A0E] border-white/5'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  isImmutable ? 'bg-[#C9A962]/20 text-[#C9A962]' : 'bg-white/5 text-[#A1A1AA]'
                }`}>
                  {getIconComponent(type.icon, 16)}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{getLocalizedField(type, 'name', lang, type.name)}</p>
                  <p className="text-[#A1A1AA] text-xs">{type.type_id}</p>
                </div>
              </div>
              {isImmutable ? (
                <div className="flex items-center gap-1.5 text-[#C9A962]/60 text-xs">
                  <Lock size={11} />
                  <span>{l('Protegido', 'Protected')}</span>
                </div>
              ) : (
                <button
                  onClick={() => handleDelete(type.type_id)}
                  className="p-1.5 rounded-lg text-[#EF4444] hover:bg-red-500/10 transition-colors"
                  data-testid={`delete-type-${type.type_id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Add new type */}
      <div className="border-t border-white/10 pt-5">
        <p className="text-[#A1A1AA] text-sm font-medium mb-4">{l('Añadir nuevo tipo', 'Add new type')}</p>
        
        {/* Name fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <input
            value={newType.name}
            onChange={e => setNewType(t => ({ ...t, name: e.target.value }))}
            placeholder="Name (EN)"
            className="bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#C9A962]"
            data-testid="new-type-name"
          />
          <input
            value={newType.name_es}
            onChange={e => setNewType(t => ({ ...t, name_es: e.target.value }))}
            placeholder="Nombre (ES)"
            className="bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-[#C9A962]"
            data-testid="new-type-name-es"
          />
        </div>

        {/* Icon picker */}
        <p className="text-[#52525B] text-xs mb-2">{l('Elige un icono:', 'Choose an icon:')}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {ICON_OPTIONS.map(({ id, Icon, label }) => (
            <button
              key={id}
              onClick={() => setNewType(t => ({ ...t, icon: id }))}
              title={label}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                newType.icon === id
                  ? 'bg-[#C9A962] text-[#0A1612] scale-110'
                  : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
              }`}
            >
              <Icon size={18} />
            </button>
          ))}
        </div>

        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#C9A962] text-[#0A1612] rounded-xl text-sm font-semibold hover:bg-[#B8984E] transition-colors"
          data-testid="add-type-btn"
        >
          <Plus size={16} />{l('Añadir Tipo', 'Add Type')}
        </button>
      </div>
    </div>
  );
}
